// Copyright 2020 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { CanvasDTO, CanvasMediaObjectLinkDTO, TargetRegionLinkProperties } from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IDeletableRepository, IRetrievableRepository } from "./IGenericRepository";

export abstract class ICanvasRepository implements IRetrievableRepository<CanvasDTO>, IDeletableRepository {
    /**
     * Retrieve a Canvas.
     * @param canvasId Id of the canvas to retrieve.
     */
    public abstract get(canvasId: string): Promise<CanvasDTO>;

    /**
     * Retrieve the MediaObjectLinks for a Canvas.
     * @param canvasId Id of the canvas to retrieve the MediaObjectLinks for.
     */
    public abstract getMediaObjectLinks(canvasId: string): Promise<CanvasMediaObjectLinkDTO[]>;

    /**
     * Gets the next page for the canvas within the canvas collection.
     * @param canvasId Id of the canvas to get the next page for.
     */
    public abstract getNextPage(canvasId: string): Promise<CanvasDTO>;

    /**
     * Gets the previous page for the canvas within the canvas collection
     * @param canvasId Id of the canvas to get the previous page for
     */
    public abstract getPreviousPage(canvasId: string): Promise<CanvasDTO>;

    /**
     * Create a new MediaObjectLink for a Canvas.
     * @param canvasId Id of the Canvas to create the MediaObjectLink for.
     * @param mediaObjectId Id of the MediaObjec to link to the Canvas.
     * @param properties Link properties to attach to the MediaObjectLink.
     */
    public abstract createMediaObjectLink(
        canvasId: string,
        mediaObjectId: string,
        properties: TargetRegionLinkProperties
    ): Promise<CanvasMediaObjectLinkDTO>;

    /**
     * Deletes a Canvas and corresponding MediaObjectLinks
     * @param canvasId Id of the Canvas to delete.
     */
    public abstract delete(canvasId: string): Promise<void>;
}

@autoinject
export class CanvasRepository implements ICanvasRepository {
    constructor(private readonly webRequestHandler: IWebRequestHandler) {}

    public get(canvasId: string): Promise<CanvasDTO> {
        return this.webRequestHandler
            .createGetRequest<CanvasDTO>(Api.canvases + "/" + canvasId)
            .send()
            .then((x) => x.content);
    }

    public getNextPage(canvasId: string): Promise<CanvasDTO> {
        return this.webRequestHandler
            .createGetRequest<CanvasDTO>(Api.canvases + "/" + canvasId + "/Next")
            .send()
            .then((x) => x.content);
    }

    public getPreviousPage(canvasId: string): Promise<CanvasDTO> {
        return this.webRequestHandler
            .createGetRequest<CanvasDTO>(Api.canvases + "/" + canvasId + "/Previous")
            .send()
            .then((x) => x.content);
    }

    public getMediaObjectLinks(canvasId: string): Promise<CanvasMediaObjectLinkDTO[]> {
        return this.webRequestHandler
            .createGetRequest<CanvasMediaObjectLinkDTO[]>(Api.canvases + "/" + canvasId + "/MediaObjectLinks")
            .send()
            .then((x) => x.content);
    }

    public createMediaObjectLink(canvasId: string, mediaObjectId: string, properties: TargetRegionLinkProperties) {
        return this.webRequestHandler
            .createPostRequest<CanvasMediaObjectLinkDTO>(Api.canvases + "/" + canvasId + "/MediaObjectLinks", {
                mediaObjectId: mediaObjectId,
                properties: properties,
            })
            .send()
            .then((x) => x.content);
    }

    public delete(canvasId: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest(Api.canvases + "/" + canvasId)
            .send()
            .then(() => {});
    }
}
